import { z } from 'zod'

// ─── Enums ──────────────────────────────────────────

export const CustomerTypeSchema = z.enum(['INDIVIDUAL', 'COMPANY'])
export const CustomerCategorySchema = z.enum(['TRANSIT', 'OWNER', 'RENTER', 'MEMBER', 'GUEST', 'OTHER'])
export const BoatTypeSchema = z.enum(['POWERBOAT', 'SAILING_BOAT', 'YACHT', 'CATAMARAN', 'FISHING', 'DINGHY', 'OTHER'])
export const VesselTypeSchema = z.enum(['PRIVATE', 'COMMERCIAL'])

// ─── Customer ───────────────────────────────────────

export const CreateCustomerSchema = z.object({
  firstName: z.string().trim().min(1, 'First name required'),
  lastName: z.string().trim().min(1, 'Last name required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')).or(z.null()),
  type: CustomerTypeSchema.default('INDIVIDUAL'),
  category: CustomerCategorySchema.default('TRANSIT'),

  phone: z.string().optional().or(z.null()),
  mobile: z.string().optional().or(z.null()),
  address: z.string().optional().or(z.null()),
  city: z.string().optional().or(z.null()),
  country: z.string().optional().or(z.null()),
  postalCode: z.string().optional().or(z.null()),
  website: z.string().optional().or(z.null()),

  idType: z.string().optional().or(z.null()),
  idNumber: z.string().optional().or(z.null()),
  idExpiry: z.string().optional().or(z.null()),
  dateOfBirth: z.string().optional().or(z.null()),
  nationality: z.string().optional().or(z.null()),

  billingName: z.string().optional().or(z.null()),
  billingAddress: z.string().optional().or(z.null()),
  billingCity: z.string().optional().or(z.null()),
  billingCountry: z.string().optional().or(z.null()),
  billingPostalCode: z.string().optional().or(z.null()),
  taxId: z.string().optional().or(z.null()),
  companyName: z.string().optional().or(z.null()),
  companyRegNumber: z.string().optional().or(z.null()),

  notes: z.string().optional().or(z.null()),
  tags: z.array(z.string()).optional(),
})

export const UpdateCustomerSchema = CreateCustomerSchema.partial()

export type CreateCustomerInput = z.infer<typeof CreateCustomerSchema>
export type UpdateCustomerInput = z.infer<typeof UpdateCustomerSchema>

// ─── Boat ───────────────────────────────────────────

export const CreateBoatSchema = z.object({
  customerId: z.string().min(1, 'Customer required'),
  name: z.string().trim().min(1, 'Name required'),
  type: BoatTypeSchema.default('OTHER'),
  loaMeters: z.number().positive('LOA must be positive'),
  beamMeters: z.number().positive('Beam must be positive'),
  vesselType: VesselTypeSchema.default('PRIVATE'),

  color: z.string().optional().or(z.null()),
  manufacturer: z.string().optional().or(z.null()),
  model: z.string().optional().or(z.null()),
  yearBuilt: z.number().int().min(1800).max(3000).optional().or(z.null()),

  draftMeters: z.number().positive().optional().or(z.null()),
  weightTons: z.number().positive().optional().or(z.null()),
  fuelCapacityL: z.number().nonnegative().optional().or(z.null()),
  waterCapacityL: z.number().nonnegative().optional().or(z.null()),

  registration: z.string().optional().or(z.null()),
  imoNumber: z.string().optional().or(z.null()),
  hullId: z.string().optional().or(z.null()),
  flagState: z.string().optional().or(z.null()),
  portOfRegistry: z.string().optional().or(z.null()),

  photo: z.string().optional().or(z.null()),
  location: z.string().optional().or(z.null()),
  notes: z.string().optional().or(z.null()),
})

export const UpdateBoatSchema = CreateBoatSchema.partial()

export type CreateBoatInput = z.infer<typeof CreateBoatSchema>
export type UpdateBoatInput = z.infer<typeof UpdateBoatSchema>

// ─── Query params ──────────────────────────────────

export const ListQuerySchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(15),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
})

export const CustomerListQuerySchema = ListQuerySchema.extend({
  category: CustomerCategorySchema.optional(),
  type: CustomerTypeSchema.optional(),
})

export const BoatListQuerySchema = ListQuerySchema.extend({
  customerId: z.string().optional(),
  type: BoatTypeSchema.optional(),
  flagState: z.string().optional(),
})

// ─── Contract ───────────────────────────────────────

export const ContractTypeSchema = z.enum(['SEASONAL', 'ANNUAL', 'MONTHLY', 'WEEKLY'])
export const ContractStatusSchema = z.enum(['DRAFT', 'PREPARED', 'PENDING_REVIEW', 'ACTIVE', 'EXPIRED', 'TERMINATED'])

export const ContractItemSchema = z.object({
  description: z.string().min(1, 'Required'),
  quantity: z.number().positive(),
  unitPriceIdr: z.number().nonnegative(),
})

// Contract currency uses the same enum as Quote/Invoice — see line ~273.
export const CreateContractSchema = z.object({
  customerId: z.string().min(1, 'Customer required'),
  boatId: z.string().nullable().optional(),
  berthId: z.string().nullable().optional(),
  type: ContractTypeSchema.default('MONTHLY'),
  startDate: z.string().min(1, 'Start date required'),
  endDate: z.string().min(1, 'End date required'),
  totalValueIdr: z.number().nonnegative(),
  depositIdr: z.number().nonnegative().nullable().optional(),
  currency: z.enum(['IDR', 'USD', 'EUR', 'AUD', 'SGD', 'JPY', 'GBP']).default('IDR'),
  fxRateToBase: z.number().positive().nullable().optional(),
  items: z.array(ContractItemSchema).default([]),
  documentUrl: z.string().nullable().optional(),
  questions: z
    .array(z.object({ q: z.string(), a: z.string().optional() }))
    .nullable()
    .optional(),
  notes: z.string().nullable().optional(),
})

export const UpdateContractSchema = CreateContractSchema.partial()

export type CreateContractInput = z.infer<typeof CreateContractSchema>
export type UpdateContractInput = z.infer<typeof UpdateContractSchema>

export const ContractListQuerySchema = ListQuerySchema.extend({
  customerId: z.string().optional(),
  status: ContractStatusSchema.optional(),
  boatId: z.string().optional(),
})

// ─── Pontoon / Berth ────────────────────────────────

export const BerthStatusSchema = z.enum(['AVAILABLE', 'OCCUPIED', 'RESERVED', 'MAINTENANCE', 'BLOCKED'])

export const CreatePontoonSchema = z.object({
  code: z.string().trim().min(1, 'Code required'),
  name: z.string().nullable().optional(),
  sortOrder: z.number().int().nonnegative().default(0),
})
export const UpdatePontoonSchema = CreatePontoonSchema.partial()
export type CreatePontoonInput = z.infer<typeof CreatePontoonSchema>
export type UpdatePontoonInput = z.infer<typeof UpdatePontoonSchema>

export const CreateBerthSchema = z.object({
  code: z.string().trim().min(1, 'Code required'),
  number: z.number().int().positive(),
  pontoonId: z.string().min(1, 'Pontoon required'),
  maxLoaMeters: z.number().positive(),
  maxBeamMeters: z.number().positive(),
  maxDraftMeters: z.number().positive(),
  depthMeters: z.number().positive(),
  hasWater: z.boolean().default(true),
  hasElectric: z.boolean().default(true),
  amperage: z.number().int().positive().nullable().optional(),
  dailyRateIdr: z.number().nonnegative(),
  monthlyRateIdr: z.number().nonnegative().nullable().optional(),
  yearlyRateIdr: z.number().nonnegative().nullable().optional(),
  mapX: z.number().nullable().optional(),
  mapY: z.number().nullable().optional(),
  mapWidth: z.number().nullable().optional(),
  mapHeight: z.number().nullable().optional(),
  mapRotation: z.number().default(0),
  status: BerthStatusSchema.default('AVAILABLE'),
  notes: z.string().nullable().optional(),
})
export const UpdateBerthSchema = CreateBerthSchema.partial()
export type CreateBerthInput = z.infer<typeof CreateBerthSchema>
export type UpdateBerthInput = z.infer<typeof UpdateBerthSchema>

export const BulkBerthPositionSchema = z.array(
  z.object({
    id: z.string().min(1),
    mapX: z.number(),
    mapY: z.number(),
    mapWidth: z.number().nullable().optional(),
    mapHeight: z.number().nullable().optional(),
    mapRotation: z.number().nullable().optional(),
  }),
)

// ─── Booking ────────────────────────────────────────

export const BookingChannelSchema = z.enum(['WALK_IN', 'RADIO', 'EMAIL', 'PHONE', 'TEXT_MESSAGE', 'AGENT', 'OTHER'])

export const BookingStatusSchema = z.enum([
  'PENDING',
  'WAITLISTED',
  'CONFIRMED',
  'DECLINED',
  'RESERVED',
  'CHECKED_IN',
  'CHECKED_OUT',
  'CANCELLED',
  'NO_SHOW',
])

export const CreateBookingSchema = z.object({
  customerId: z.string().min(1, 'Customer required'),
  boatId: z.string().min(1, 'Boat required'),
  berthId: z.string().nullable().optional(),
  checkInAt: z.string().min(1, 'Check-in required'),
  checkOutAt: z.string().min(1, 'Check-out required'),
  channel: BookingChannelSchema,
  channelLocation: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  totalAmountIdr: z.number().nonnegative().nullable().optional(),
})

export const UpdateBookingSchema = CreateBookingSchema.partial()

export type CreateBookingInput = z.infer<typeof CreateBookingSchema>
export type UpdateBookingInput = z.infer<typeof UpdateBookingSchema>

export const BookingListQuerySchema = ListQuerySchema.extend({
  status: BookingStatusSchema.optional(),
  customerId: z.string().optional(),
  boatId: z.string().optional(),
  berthId: z.string().optional(),
  channel: BookingChannelSchema.optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  view: z.enum(['requests', 'bookings']).optional(),
})

export const AssignBerthSchema = z.object({
  berthId: z.string().min(1),
})

export const CheckInSchema = z.object({
  at: z.string().optional(),
  berthId: z.string().optional(),
  notes: z.string().optional(),
})

export const CheckOutSchema = z.object({
  at: z.string().optional(),
  notes: z.string().optional(),
})

export const DeclineSchema = z.object({
  reason: z.string().min(1, 'Reason required'),
})

// ─── Absence ────────────────────────────────────────

export const CreateAbsenceSchema = z.object({
  berthId: z.string().min(1),
  departure: z.string().min(1),
  returnDate: z.string().min(1),
  reason: z.string().nullable().optional(),
  status: z.enum(['AVAILABLE', 'BLOCKED']).default('AVAILABLE'),
})

export type CreateAbsenceInput = z.infer<typeof CreateAbsenceSchema>

// ─── Quote ──────────────────────────────────────────

export const QuoteStatusSchema = z.enum(['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'CONVERTED'])

export const QuoteLineItemSchema = z.object({
  description: z.string().min(1, 'Required'),
  quantity: z.number().positive(),
  unitPriceIdr: z.number().nonnegative(),
})

export const CurrencyCodeSchema = z.enum(['IDR', 'USD', 'EUR', 'AUD', 'SGD', 'JPY', 'GBP']).default('IDR')

export const CreateQuoteSchema = z.object({
  customerId: z.string().min(1, 'Customer required'),
  boatId: z.string().nullable().optional(),
  bookingId: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  taxRate: z.number().min(0).max(100).default(11),
  currency: CurrencyCodeSchema,
  fxRateToBase: z.number().positive().nullable().optional(),
  notes: z.string().nullable().optional(),
  lineItems: z.array(QuoteLineItemSchema).default([]),
})
export const UpdateQuoteSchema = CreateQuoteSchema.partial()
export type CreateQuoteInput = z.infer<typeof CreateQuoteSchema>
export type UpdateQuoteInput = z.infer<typeof UpdateQuoteSchema>

export const QuoteListQuerySchema = ListQuerySchema.extend({
  customerId: z.string().optional(),
  status: QuoteStatusSchema.optional(),
})

export const ConvertQuoteToInvoiceSchema = z.object({
  dueDate: z.string().min(1, 'Due date required'),
})

// ─── Invoice ────────────────────────────────────────

export const InvoiceStatusSchema = z.enum([
  'PRO_FORMA',
  'DRAFT',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'CANCELLED',
])

export const InvoiceLineItemSchema = QuoteLineItemSchema

export const CreateInvoiceSchema = z.object({
  customerId: z.string().min(1),
  boatId: z.string().nullable().optional(),
  bookingId: z.string().nullable().optional(),
  dueDate: z.string().min(1, 'Due date required'),
  taxRate: z.number().min(0).max(100).default(11),
  currency: CurrencyCodeSchema,
  fxRateToBase: z.number().positive().nullable().optional(),
  notes: z.string().nullable().optional(),
  recipientName: z.string().nullable().optional(),
  recipientEmail: z.string().email().nullable().optional().or(z.literal('')),
  recipientAddress: z.string().nullable().optional(),
  recipientTaxId: z.string().nullable().optional(),
  recipientCompanyName: z.string().nullable().optional(),
  lineItems: z.array(InvoiceLineItemSchema).default([]),
})
export const UpdateInvoiceSchema = CreateInvoiceSchema.partial()
export type CreateInvoiceInput = z.infer<typeof CreateInvoiceSchema>
export type UpdateInvoiceInput = z.infer<typeof UpdateInvoiceSchema>

export const InvoiceListQuerySchema = ListQuerySchema.extend({
  customerId: z.string().optional(),
  status: InvoiceStatusSchema.optional(),
  overdue: z
    .string()
    .transform((s) => s === 'true')
    .optional(),
})

// ─── Payment ────────────────────────────────────────

export const PaymentMethodSchema = z.enum(['CASH', 'BANK_TRANSFER', 'CARD', 'CHECK', 'OTHER'])

export const CreatePaymentSchema = z.object({
  amountIdr: z.number().positive(),
  method: PaymentMethodSchema.default('BANK_TRANSFER'),
  paidAt: z.string().optional(),
  reference: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
})
export type CreatePaymentInput = z.infer<typeof CreatePaymentSchema>

// ─── Assignment (Staff Task / Work Order) ───────────

export const AssignmentStatusSchema = z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'BLOCKED', 'CANCELLED'])
export const AssignmentPrioritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT'])
export const AssignmentCategorySchema = z.enum([
  'CUSTOMER_SUPPORT',
  'MAINTENANCE',
  'CLEANING',
  'ADMIN',
  'SECURITY',
  'OTHER',
])
export const AssignmentReferenceTypeSchema = z.enum(['BOOKING', 'CUSTOMER', 'BOAT', 'INVOICE', 'CONTRACT', 'OTHER'])

export const CreateAssignmentSchema = z.object({
  title: z.string().trim().min(1, 'Title required'),
  description: z.string().nullable().optional(),
  status: AssignmentStatusSchema.nullable().optional(),
  priority: AssignmentPrioritySchema.nullable().optional(),
  category: AssignmentCategorySchema.nullable().optional(),
  startDate: z.string().nullable().optional(),
  deadline: z.string().nullable().optional(),
  assignedStaffId: z.string().nullable().optional(),
  referenceType: AssignmentReferenceTypeSchema.nullable().optional(),
  referenceId: z.string().nullable().optional(),
})

export const UpdateAssignmentSchema = CreateAssignmentSchema.partial()
export type CreateAssignmentInput = z.infer<typeof CreateAssignmentSchema>
export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentSchema>

export const AssignmentListQuerySchema = ListQuerySchema.extend({
  status: AssignmentStatusSchema.optional(),
  priority: AssignmentPrioritySchema.optional(),
  category: AssignmentCategorySchema.optional(),
  assignedStaffId: z.string().optional(),
})

// ─── Inventory ──────────────────────────────────────

export const CreateInventoryItemSchema = z.object({
  name: z.string().trim().min(1, 'Name required'),
  category: z.string().nullable().optional(),
  initialStock: z.number().nonnegative(),
  currentStock: z.number().nonnegative().optional(),
  lowThreshold: z.number().nonnegative().default(0),
  unit: z.string().default('pcs'),
  notes: z.string().nullable().optional(),
})
export const UpdateInventoryItemSchema = CreateInventoryItemSchema.partial()
export type CreateInventoryItemInput = z.infer<typeof CreateInventoryItemSchema>
export type UpdateInventoryItemInput = z.infer<typeof UpdateInventoryItemSchema>

export const AdjustStockSchema = z.object({
  delta: z.number(),
  reason: z.string().nullable().optional(),
})
export type AdjustStockInput = z.infer<typeof AdjustStockSchema>

// ─── Chat ───────────────────────────────────────────

export const ConversationKindSchema = z.enum(['DIRECT', 'GROUP', 'SUPPORT'])

export const CreateConversationSchema = z.object({
  kind: ConversationKindSchema.default('DIRECT'),
  title: z.string().nullable().optional(),
  participantUserIds: z.array(z.string()).min(1, 'At least one participant required'),
})
export type CreateConversationInput = z.infer<typeof CreateConversationSchema>

export const SendMessageSchema = z.object({
  body: z.string().trim().min(1, 'Message body required'),
  attachments: z
    .array(
      z.object({
        url: z.string(),
        filename: z.string(),
        size: z.number().int().nonnegative(),
        mime: z.string(),
      }),
    )
    .optional(),
})
export type SendMessageInput = z.infer<typeof SendMessageSchema>

// ─── Settings ───────────────────────────────────────

export const UpdateMarinaSettingsSchema = z.object({
  name: z.string().trim().min(1).optional(),
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  currency: z.string().optional(),
  defaultTaxRate: z.number().min(0).max(100).optional(),
  logoUrl: z.string().nullable().optional(),
  supportEmail: z.string().email().nullable().optional().or(z.literal('')),
  supportPhone: z.string().nullable().optional(),
})
export type UpdateMarinaSettingsInput = z.infer<typeof UpdateMarinaSettingsSchema>
